import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { auth, type UserMe } from "@/api/auth";
import { TokenStore } from "@/api/http";

/** JWT claims from the backend's TenantJWTStrategy: `sub`, `org_id`, `reg`, `sup`. */
interface TokenClaims {
  sub: string;
  org_id: string;
  exp?: number;
}

function decodeJwt(token: string): TokenClaims | null {
  try {
    const [, payload] = token.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as TokenClaims;
  } catch {
    return null;
  }
}

interface AuthValue {
  user: UserMe | null;
  claims: TokenClaims | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth used outside AuthProvider");
  return v;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => TokenStore.get());
  const [user, setUser] = useState<UserMe | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!token);
  const navigate = useNavigate();
  const location = useLocation();

  const claims = useMemo<TokenClaims | null>(() => (token ? decodeJwt(token) : null), [token]);

  const refresh = useCallback(async () => {
    if (!TokenStore.get()) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    try {
      setUser(await auth.me());
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Token expiry (dispatched by http.ts on 401).
  useEffect(() => {
    function onExpired() {
      setToken(null);
      setUser(null);
      navigate("/login", { state: { returnTo: location.pathname }, replace: true });
    }
    window.addEventListener("piggy:auth-expired", onExpired);
    return () => window.removeEventListener("piggy:auth-expired", onExpired);
  }, [navigate, location.pathname]);

  const login: AuthValue["login"] = async (email, password, totpCode) => {
    const res = await auth.login(email, password, totpCode);
    TokenStore.set(res.access_token);
    setToken(res.access_token);
    setUser(await auth.me());
  };

  const logout = useCallback(() => {
    TokenStore.clear();
    setToken(null);
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  const value: AuthValue = {
    user,
    claims,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refresh,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
